import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

// ── Query ─────────────────────────────────────────────────────────────────────

export const booksHealthQuerySchema = z.object({
  // comma-separated company UUIDs
  companyIds: z
    .string()
    .min(1)
    .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
    .pipe(z.array(z.string().uuid()).min(1)),
  /** Defaults to today (server date) when omitted. */
  asOf: isoDate.optional(),
});
export type BooksHealthQuery = z.infer<typeof booksHealthQuerySchema>;

// ── Response ──────────────────────────────────────────────────────────────────

/**
 * `fail` = the books are provably wrong or tamper-evident state is broken.
 * `warn` = something needs attention but nothing is provably incorrect.
 */
export type HealthSeverity = 'ok' | 'warn' | 'fail';

export interface HealthFinding {
  companyId: string;
  companyName: string;
  /** One line describing what is wrong for this company. */
  detail: string;
  /** Frontend path that lets the owner act on this finding. */
  link?: string;
}

export interface HealthCheck {
  /** Stable key — safe to reference in tests and the frontend. */
  id: string;
  label: string;
  severity: HealthSeverity;
  /** One-line roll-up, shown whether or not there are findings. */
  summary: string;
  /** Per-company detail. Empty when severity is `ok`. */
  findings: HealthFinding[];
}

export interface BooksHealthResponse {
  asOf: string;
  companyCount: number;
  /** Worst severity across all checks. */
  overall: HealthSeverity;
  checks: HealthCheck[];
}
